import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Typography, Space, Button, Table, Tag, Modal, Form,
  Input, Select, message, Statistic, Row, Col, Divider, Alert
} from 'antd';
import { MailOutlined, PlusOutlined, SendOutlined, ReloadOutlined } from '@ant-design/icons';
import Navigation from '../components/Navigation';
import axios from 'axios';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const API = 'https://limitverify.ru:5000/api/mailings';

const statusColor = {
  draft: 'default',
  sending: 'processing',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning'
};

const statusLabel = {
  draft: 'Черновик',
  sending: 'Отправляется',
  completed: 'Завершена',
  failed: 'Ошибка',
  cancelled: 'Отменена'
};

const Mailings = () => {
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [segRes, campRes] = await Promise.all([
        axios.get(`${API}/segments`, { headers }),
        axios.get(`${API}/campaigns`, { headers })
      ]);
      if (segRes.data.success) setSegments(segRes.data.segments || []);
      if (campRes.data.success) setCampaigns(campRes.data.campaigns || []);
    } catch (e) {
      message.error(e.response?.data?.error || 'Ошибка загрузки рассылок');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onSegmentChange = async (segment) => {
    setPreviewCount(null);
    try {
      const res = await axios.get(`${API}/segments/preview`, {
        headers,
        params: { segment }
      });
      if (res.data.success) setPreviewCount(res.data.count);
    } catch (_) {
      setPreviewCount(null);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await axios.post(`${API}/campaigns`, values, { headers });
      if (!res.data.success) {
        message.error(res.data.error || 'Не удалось создать');
        return;
      }
      message.success(`Кампания создана, получателей: ${res.data.campaign.totalRecipients}`);
      setCreateOpen(false);
      form.resetFields();
      setPreviewCount(null);
      await loadAll();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.response?.data?.error || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = (campaign) => {
    Modal.confirm({
      title: 'Запустить рассылку?',
      content: (
        <div>
          <p><b>{campaign.title}</b></p>
          <p>Сегмент: {campaign.segmentLabel}</p>
          <p>Получателей: {campaign.totalRecipients}</p>
          <p>Отправка идёт с паузой ~2 сек между письмами (бережём репутацию IP).</p>
        </div>
      ),
      okText: 'Отправить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const res = await axios.post(`${API}/campaigns/${campaign.id}/send`, null, { headers });
          if (res.data.success) {
            message.success('Рассылка запущена');
            await loadAll();
          } else {
            message.error(res.data.error || 'Не удалось запустить');
          }
        } catch (e) {
          message.error(e.response?.data?.error || 'Ошибка запуска');
        }
      }
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'Название', dataIndex: 'title' },
    { title: 'Тема', dataIndex: 'subject', ellipsis: true },
    { title: 'Сегмент', dataIndex: 'segmentLabel', width: 180 },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 130,
      render: (s) => <Tag color={statusColor[s] || 'default'}>{statusLabel[s] || s}</Tag>
    },
    {
      title: 'Прогресс',
      width: 140,
      render: (_, r) => `${r.sentCount || 0}/${r.totalRecipients || 0}` +
        (r.failedCount ? ` (ошибок ${r.failedCount})` : '')
    },
    {
      title: '',
      width: 140,
      render: (_, r) => (
        (r.status === 'draft' || r.status === 'failed') && r.totalRecipients > 0 ? (
          <Button type="primary" size="small" icon={<SendOutlined />} onClick={() => handleSend(r)}>
            Отправить
          </Button>
        ) : null
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Navigation />
      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            <MailOutlined style={{ marginRight: 8 }} />
            Рассылки
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>Обновить</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Новая кампания
            </Button>
          </Space>
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Сегменты строятся из клиентов в CRM"
          description="Учитываются newsletter_subscribed и тогглы уведомлений в кабинете клиента (Новости бренда / События / Напоминания). Внешний ESP не используется — письма уходят через Stalwart."
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          {segments.map((s) => (
            <Col key={s.key} xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic title={s.label} value={s.count} suffix="чел." />
                <Text type="secondary" style={{ fontSize: 12 }}>{s.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>

        <Card title="Кампании">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={campaigns}
            pagination={{ pageSize: 20 }}
          />
        </Card>

        <Modal
          title="Новая рассылка"
          open={createOpen}
          onCancel={() => { setCreateOpen(false); form.resetFields(); setPreviewCount(null); }}
          onOk={handleCreate}
          confirmLoading={submitting}
          okText="Создать черновик"
          width={720}
          destroyOnClose
        >
          <Form form={form} layout="vertical" initialValues={{ segment: 'all_subscribed' }}>
            <Form.Item name="title" label="Название кампании" rules={[{ required: true, message: 'Укажите название' }]}>
              <Input placeholder="Апрельские новости" />
            </Form.Item>
            <Form.Item name="segment" label="Сегмент" rules={[{ required: true }]}>
              <Select
                options={segments.map(s => ({ value: s.key, label: `${s.label} (${s.count})` }))}
                onChange={onSegmentChange}
              />
            </Form.Item>
            {previewCount !== null && (
              <Paragraph>Получателей в сегменте: <b>{previewCount}</b></Paragraph>
            )}
            <Form.Item name="subject" label="Тема письма" rules={[{ required: true, message: 'Укажите тему' }]}>
              <Input placeholder="Новости LimitVerify" />
            </Form.Item>
            <Form.Item
              name="bodyHtml"
              label="Текст письма (HTML или текст)"
              rules={[{ required: true, message: 'Введите текст' }]}
            >
              <TextArea rows={10} placeholder="<p>Здравствуйте!</p><p>...</p>" />
            </Form.Item>
            <Form.Item name="bodyText" label="Текстовая версия (опционально)">
              <TextArea rows={4} placeholder="Если пусто — сгенерируется из HTML" />
            </Form.Item>
          </Form>
          <Divider />
          <Text type="secondary">
            Черновик сразу фиксирует список получателей. Запуск — кнопкой «Отправить» в таблице.
          </Text>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Mailings;
